<template>
  <article
    v-if="product && selectedVariant"
    id="product"
    class="mx-auto my-6 text-neutral-900 md:max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl mobile:mt-0"
  >
    <KikBreadcrumbs :breadcrumbs="breadcrumbs" />
    <div class="flex w-full flex-col justify-center gap-8 mobile:gap-0 desktop:flex-row">
      <div class="mobile:w-full">
        <KikGallery
          v-if="productGallery?.length"
          :images="productGallery"
          :sale-tag="saleTag"
          :new-tag="newTag"
          :online-only-tag="onlineOnlyTag"
          :online-sale="isOnlineSale"
          :marketing-tags="marketingTags"
        />
      </div>
      <div class="w-full mobile:p-4 desktop:max-w-[710px]">
        <ProductInformation
          :product="product"
          :variant-sku="sku"
          :sale-tag="saleTag"
          :is-my-kik-discount="isMyKikDiscount"
          @open-safety-information="openSafetyInformation"
        />
        <VariantSelection
          :variants="filteredVariants"
          :sizes="sizes"
          :colors="colors"
          :show-alert="showAlert"
          :is-uni-size="isUniSize"
          @change-variant="changeVariantData"
          @change-to-dummy-variant="changeVariantData"
        />
        <template v-if="$flags.hasnt('shop.catalog.enabled')">
          <ShippingInformation />
        </template>
        <template v-if="$flags.has('store.stock.availability')">
          <FilialStockInformation
            :preferred-filial-cookie="preferredFilialCookie"
            :stock-in-filial="stockInFilial"
            :sku="selectedVariant.sku"
          />
        </template>
        <template v-if="$flags.hasnt('shop.catalog.enabled')">
          <ShopBenefits />
        </template>

        <AdditionalInformation
          v-if="product"
          ref="additionalInformation"
          :description="product.description"
          :variant="selectedVariant"
          :product-number="product.key"
        />
      </div>
    </div>

    <SharedTeaserSliderRecoSliderWrapper
      recommender-type="pdp"
      :product-id="product.key"
      :fallback-category="currentCategory"
    />
    <KikEtrustedWidget
      v-if="$flags.has('product.rating.review')"
      id="pdp"
      class="mt-8 p-5"
      :sku="product.key"
    />
  </article>
  <KikSkeletonProduct v-else />
</template>

<script setup lang="ts">
import { useTracking } from '../composables/custom/useTracking';
import { useUser } from '../composables/custom/useUser/useUser';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import { useAlert } from '~/composables/custom/useProduct/useAlert';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import productGetters from '~/composables/getters/productGetters';
import { Attributes } from '~/components/Product/types/product';
import type { Variant } from '~/components/Product/types/product';
import ProductInformation from '~/components/Product/ProductInformation.vue';
import VariantSelection from '~/components/Product/VariantSelection.vue';
import ShopBenefits from '~/components/Product/ShopBenefits.vue';
import ShippingInformation from '~/components/Product/ShippingInformation.vue';
import AdditionalInformation from '~/components/Product/AdditionalInformation.vue';
import { useScrollStore } from '~/store/scroll';
import { useTagsLoader } from '~/composables/custom/useTagLoader/useTagLoader';
import FilialStockInformation from '~/components/Product/FilialStockInformation.vue';
import { useSSR } from '~/composables/useSSR/useSSR';

const route = useRoute();

definePageMeta({
  layout: 'default',
});

useSeoMeta({
  robots: false,
});

useHead({
  script: [
    {
      src: 'https://scripts.publitas.com/kik/e-com/product.js',
      type: 'text/javascript',
    },
  ],
});

// TBD: extract this since it's already being set in a different place
const UC_UI_INITIALIZED_EVENT = 'UC_UI_INITIALIZED';

const attributeNames: string[] = [
  Attributes.WGR,
  Attributes.EKB,
  Attributes.SIZE,
  Attributes.SIZE_ORDER,
  Attributes.COLOR_CODE,
  Attributes.COLOR,
  Attributes.DANGER_ICONS,
  Attributes.MANUFACTURER_INFOS,
  Attributes.SIGNAL_WORD,
  Attributes.HAZARD_NOTICE,
  Attributes.MATERIAL,
  Attributes.BULLET_POINTS,
  Attributes.SIEGEL,
  Attributes.PRODUCT_DATA_SHEETS,
  Attributes.ENERGY_EFFICIENCY_CLASS,
  Attributes.ENERGY_EFFICIENCY_LABEL,
  Attributes.DETAIL_NAME,
  Attributes.BRAND,
  Attributes.CAPACITY_QUANTITY,
  Attributes.CAPACITY_UOM,
  Attributes.SEALS,
  Attributes.COLOR_NUMBER,
  Attributes.DISCOUNT_SALE_VALUE,
  Attributes.MARKETING_TAG,
  Attributes.LICENCE,
];

const { locale } = useAppLocale();
const { $flags } = useNuxtApp();
const { showAlert, resetAlert } = useAlert();
const { isAuthenticated } = useUser();
const scroll = useScrollStore();
const {
  trackProductView,
  trackPage,
} = useTracking();
const { setHrefLangForProduct } = useHrefLang();
const { setProductInfo, setOverallInfo } = useSeo();
const { fetchTags } = useTagsLoader();
const { onSSR } = useSSR();

const {
  sku,
  selectedColor,
  selectedVariant,
  setSelectedVariant,
  resetProduct,
  getStockAndPrice,
  getStockInStore,
  stockInFilial,
  productAvailabilityAndStock,
  breadcrumbs,
  product,
  currentCategory,
  getProductById,
  getProductToTrack,
} = useProduct(route.path);

const additionalInformation = ref();

watch(isAuthenticated, async () => {
  await getStockAndPrice({ products: [product.value?.id] });
});

interface FilialAddress {
  company: string;
  postalCode: string;
  city: string;
  country: string;
  externalId: string;
};

const preferredFilialCookie = useCookie<FilialAddress>('sf-favorite-store');
const colors = computed(() => product.value?.colors?.map(({ label }: { label: string }) => label) ?? []);
const openSafetyInformation = () => {
  additionalInformation.value?.openSafetyInformation();
};

const filteredVariants = computed<Variant[]>(() => product.value?.variants || []);

const isUniSize = computed(() => (product.value?.sizes?.[selectedColor.value as string]?.length ?? 0) <= 1);

const masterVariant = computed(() => {
  const colorToFind = selectedColor.value ?? colors.value[0];
  return filteredVariants.value?.find(({ attributes }) => attributes.color?.toLowerCase() === colorToFind?.toLowerCase());
});

const firstVariantWithStock = computed(() => filteredVariants.value?.find((variant: any) => variant.availability.isOnStock && variant.attributes.online));
// if color code in the URL, then preselect the matching color-variant
const preselectUrlVariant = () => {
  const slug = route.params.slug as string;

  if (!slug || !/.*-\d*$/.test(slug)) {
    return undefined;
  }

  const colorCode = slug.split('-').pop();

  if (!colorCode) {
    return undefined;
  }

  let selectedUrlVariant = filteredVariants.value.find(variant => variant.attributes.color_code?.toString() === colorCode && variant.availability.isOnStock && variant.attributes.online);

  // fallback to first variant with stock
  if (!selectedUrlVariant && isUniSize.value) {
    selectedUrlVariant = firstVariantWithStock.value;
  }

  // in case of out of stock fallback to mastervariant if exists or to the 1st element in the list so that we can show some information about the product
  selectedUrlVariant = selectedUrlVariant || masterVariant.value || filteredVariants.value?.[0];

  changeVariantData(selectedUrlVariant);

  return undefined;
};

const changeVariantData = (variant: Variant) => {
  if (!variant || !product.value) {
    return;
  }

  resetAlert();
  setSelectedVariant(variant);
  setHrefLangForProduct(selectedVariant.value?.hrefLang);

  if (import.meta.server) {
    return;
  }

  window.history.replaceState(
    {},
    '',
    productGetters.getProductUrl({ key: product.value?.key, hrefLang: selectedVariant.value?.hrefLang }, locale, route.query),
  );
};

onSSR(async () => {
  await getProductById(route.params?.id as string, attributeNames);
  await fetchTags();

  preselectUrlVariant();

  if (!product.value) {
    return;
  }

  if (route.query?.size) {
    const variant = product.value?.variants.find((v: Variant) => v.attributes.size === route.query?.size && v.availability.isOnStock && v.attributes.online);
    changeVariantData(variant);
  }

  if (import.meta.client) {
    await getStockAndPrice({ products: [product.value?.id] });
  }
  setProductInfo({ product, selectedVariant, productGallery });
});

onBeforeMount(async () => {
  resetProduct();
  if (!Object.keys(productAvailabilityAndStock.value || {}).length && product.value?.id) {
    await getStockAndPrice({ products: [product.value.id] });
  }
  changeVariantData(selectedVariant.value);
  setProductInfo({ product, selectedVariant, productGallery });
});

onBeforeRouteLeave((_to, _from, next) => {
  scroll.setScroll(product.value?.id);

  next();
});

const startProductView = () => {
  trackPage();
  const productToTrack = getProductToTrack(1);
  trackProductView(productToTrack);
};

onBeforeUnmount(() => {
  window.removeEventListener(UC_UI_INITIALIZED_EVENT, startProductView);
});

watch(selectedVariant, () => {
  getStockInStore({ storeId: preferredFilialCookie.value?.externalId, skus: [selectedVariant.value.sku] });
  setHrefLangForProduct(selectedVariant.value?.hrefLang);

  window.addEventListener(UC_UI_INITIALIZED_EVENT, startProductView);

  if (window.UC_UI?.isInitialized()) {
    startProductView();
  }
});

const productGallery = computed(() => selectedVariant.value?.images || []);
const sizes = computed(() => product.value?.sizes);
const newTag = computed(() => product.value?.isNew);
const saleTag = computed(() => selectedVariant.value?.attributes?.discount_sale_value && !selectedVariant.value?.price?.hideDiscountSaleValue ? Number(selectedVariant.value?.attributes?.discount_sale_value) : undefined);
const onlineOnlyTag = computed(() => product.value?.isOnlineOnly as boolean);
const isMyKikDiscount = computed(() => product.value?.isMyKikDiscount as boolean);
const isOnlineSale = computed(() => selectedVariant.value?.isOnlineSale);
const marketingTags = computed(() => selectedVariant.value?.attributes?.marketing_tag as any[] ?? []);

onBeforeUnmount(() => {
  setOverallInfo();
});
</script>
